import { useCallback, useEffect, useState } from 'react';

import {
  useFloating,
  flip,
  shift,
  offset,
  hide,
  useHover,
  useInteractions,
  Placement,
  OffsetOptions,
  safePolygon,
  useFocus,
} from '@floating-ui/react';

import { noop } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import { getAllNestedKeys, getSelectedItems } from '../helpers/helpers';
import { MenuCategoryType, MenuItemType } from '../helpers/types';

const defaultOffset: OffsetOptions = { mainAxis: 0, crossAxis: -10 };
const reversedOffset: OffsetOptions = { mainAxis: 0, crossAxis: 10 };

const checkHasSelectedNested = <TKey extends string | number>(
  nested: MenuItemType<TKey>['nested'],
  value: TKey[],
) => {
  if (!nested) {
    return false;
  }
  const allNestedKeys = getAllNestedKeys(nested);
  return allNestedKeys?.some((k) => value.includes(k));
};

type UseOverlayMenuItemProps<TKey extends string | number> = {
  itemKey: TKey;
  placement: Placement | undefined;
  disabled: boolean | undefined;
  value: TKey[] | undefined;
  nested: MenuCategoryType<TKey>[] | undefined;
  category: MenuCategoryType<TKey> | undefined;
  onChange: undefined | ((arr: TKey[]) => void);
  onClose: undefined | (() => void);
  onClick: React.MouseEventHandler<HTMLButtonElement> | undefined;
  /* Options that modify how the safe polygon for nested menus behaves (see floating-ui documentation https://floating-ui.com/docs/useHover#safepolygon) */
  safePolygonArgs?: Parameters<typeof safePolygon>[0] & { enabled?: boolean };
};

export default <TKey extends string | number>({
  itemKey,
  placement: placementProp,
  disabled,
  value = [],
  nested,
  category,
  onClick: onClickProp,
  onChange = noop,
  onClose,
  safePolygonArgs = {
    enabled: true,
    // This blocks the hover events on adjacent items from triggering when moving the cursor to the nested menu
    // Without this, the safe triangle is not as effective
    blockPointerEvents: true,
  },
}: UseOverlayMenuItemProps<TKey>) => {
  const [offsetValue, setOffsetValue] = useState(defaultOffset);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (placementProp !== useOverlay.PLACEMENTS.RIGHT_START) {
      setOffsetValue(reversedOffset);
    }
  }, [placementProp]);

  const { context, refs, floatingStyles } = useFloating<HTMLButtonElement>({
    placement: placementProp,
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(offsetValue), flip(), shift(), hide()],
  });

  const enabledWithChildren = !disabled && !!nested;

  const focus = useFocus(context, { enabled: enabledWithChildren });

  const hover = useHover(context, {
    enabled: enabledWithChildren,
    handleClose: safePolygonArgs.enabled
      ? safePolygon(safePolygonArgs)
      : undefined,
  });

  const interactions = useInteractions([focus, hover]);

  const selected = value.includes(itemKey);
  const hasSelectedNested = checkHasSelectedNested(nested, value);

  const handleClose = useCallback(
    (cat: MenuCategoryType<TKey>) =>
      onClose && cat.showSelection === false && onClose(),
    [onClose],
  );

  const add = useCallback(
    (eventKey: TKey, cat: MenuCategoryType<TKey>) => {
      onChange(getSelectedItems(value, eventKey, cat));
    },
    [onChange, value],
  );

  const remove = useCallback(
    (eventKey: TKey) => {
      onChange(value.filter((k) => k !== eventKey));
    },
    [onChange, value],
  );

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (nested) {
        return;
      }

      if (onClickProp) {
        onClickProp(e);
      }

      if (!category) return;
      if (category.allowReselect) {
        add(itemKey, category);
      } else {
        if (selected) {
          remove(itemKey);
        } else {
          add(itemKey, category);
        }
      }
      handleClose(category);
    },
    [
      add,
      category,
      handleClose,
      itemKey,
      nested,
      onClickProp,
      remove,
      selected,
    ],
  );

  const getOverlayMenuItemProps = useCallback<
    typeof interactions.getReferenceProps
  >(
    (userProps) => ({
      ...interactions.getReferenceProps({
        ...userProps,
        onClick,
        selected: selected || hasSelectedNested,
        ref: refs.setReference,
      }),
    }),
    [hasSelectedNested, interactions, onClick, refs.setReference, selected],
  );

  const getNestedMenuProps = useCallback<typeof interactions.getFloatingProps>(
    (userProps) =>
      interactions.getFloatingProps({
        ...userProps,
        ref: refs.setFloating,
        style: Object.assign(floatingStyles, userProps?.style),
        onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          event.stopPropagation();
          userProps?.onClick?.(event);
        },
      }),
    [floatingStyles, interactions, refs.setFloating],
  );
  return {
    getOverlayMenuItemProps,
    getNestedMenuProps,
    isOpen,
    selected,
  };
};
